import ReturnCasePortfolioPage from '@/pages/assessments/ReturnCasePortfolioPage'
import ClientOwnedAssessmentHistory from './ClientOwnedAssessmentHistory'
import { isClientOwnedAssessmentPagesEnabled, isReturnPreparationV2Enabled } from '@/config/runtime'
/**
 * CustomerAssessmentsTab
 *
 * Loads a customer's income-tax (ITR) assessments from the assessments service
 * (by `customer.id`) and renders them grouped by financial year with status
 * badges. "Start New Assessment" opens the create modal; "View Details" /
 * "Continue" navigate to the assessment detail page.
 */

import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ChevronDown,
  ChevronRight,
  Plus,
  Eye,
  FileText,
  AlertCircle,
  CheckCircle,
  Clock,
  Check,
  Link2,
} from 'lucide-react'
import { getAssessmentService } from '@/services/assessments'
import { toast } from '@/store/toastStore'
import AssessmentModal from '@/components/customers/AssessmentModal'
import RecordFilingModal, {
  RecordFilingFormValues,
} from '@/components/assessments/RecordFilingModal'
import LinkDocumentModal from '@/components/assessments/LinkDocumentModal'
import { ASSESSMENT_STATUS_FLOW, ASSESSMENT_STATUS_LABEL } from '@/types/assessment.types'
import type { Assessment, AssessmentStatus } from '@/types/assessment.types'
import type { IndividualCustomer } from '@/types/customer'
import { useAuth } from '@/contexts/AuthContext'
import {
  legacyAssessmentReadOnlyMessage,
  legacyAssessmentWritesEnabled,
} from '@/config/assessmentCutover'

interface CustomerAssessmentsTabProps {
  customer: IndividualCustomer
  /** When true (set by the Overview Quick Action), open the create modal on mount. */
  autoCreate?: boolean
  /** Called once the auto-open has been handled, so the parent can clear its flag. */
  onAutoCreateHandled?: () => void
}

const STATUS_CONFIG: Record<
  AssessmentStatus,
  { label: string; className: string; icon: React.ElementType }
> = {
  not_started: {
    label: 'Not Started',
    className: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400',
    icon: Clock,
  },
  documents_pending: {
    label: 'Documents Pending',
    className: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
    icon: AlertCircle,
  },
  in_progress: {
    label: 'In Progress',
    className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    icon: Clock,
  },
  under_review: {
    label: 'Under Review',
    className: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
    icon: Eye,
  },
  filed: {
    label: 'Filed',
    className: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    icon: CheckCircle,
  },
  processed: {
    label: 'Processed',
    className: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    icon: CheckCircle,
  },
  refund_issued: {
    label: 'Refund Issued',
    className: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
    icon: CheckCircle,
  },
  demand_raised: {
    label: 'Demand Raised',
    className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    icon: AlertCircle,
  },
}

function StatusBadge({ status }: { status: AssessmentStatus }) {
  const { label, className, icon: Icon } = STATUS_CONFIG[status]
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${className}`}
    >
      <Icon className="w-3 h-3" />
      {label}
    </span>
  )
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(amount)
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

function LegacyCustomerAssessmentsTab({
  customer,
  autoCreate,
  onAutoCreateHandled,
}: CustomerAssessmentsTabProps) {
  const { hasPermission } = useAuth()
  const canCreate = legacyAssessmentWritesEnabled && hasPermission('txh.assessment.create')
  const canUpdate = legacyAssessmentWritesEnabled && hasPermission('txh.assessment.update')
  const canUpdateStatus =
    legacyAssessmentWritesEnabled && hasPermission('txh.assessment.status.update')
  const canFile = legacyAssessmentWritesEnabled && hasPermission('txh.assessment.file')
  const canApprove = hasPermission('txh.assessment.approve')
  const navigate = useNavigate()
  const [assessments, setAssessments] = useState<Assessment[]>([])
  const [loading, setLoading] = useState(true)
  const [expandedYears, setExpandedYears] = useState<Set<string>>(new Set())
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [filingAssessment, setFilingAssessment] = useState<Assessment | null>(null)
  const [linkingAssessment, setLinkingAssessment] = useState<Assessment | null>(null)
  const [advancingId, setAdvancingId] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const list = await getAssessmentService().listForCustomer(customer.id)
      setAssessments(list)
      // Expand the most recent year by default.
      const years = list.map(a => a.financialYear)
      const newest = years.sort((a, b) => Number(b.split('-')[0]) - Number(a.split('-')[0]))[0]
      if (newest) setExpandedYears(new Set([newest]))
    } catch (err) {
      toast.error(
        'Could not load assessments',
        err instanceof Error ? err.message : 'Please try again.'
      )
    } finally {
      setLoading(false)
    }
  }, [customer.id])

  useEffect(() => {
    load()
  }, [load])

  // Honor the Overview "Start New Assessment" Quick Action: open the modal once.
  useEffect(() => {
    if (autoCreate) {
      if (canCreate) setIsModalOpen(true)
      onAutoCreateHandled?.()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoCreate])

  const toggleYear = (year: string) => {
    setExpandedYears(prev => {
      const next = new Set(prev)
      if (next.has(year)) next.delete(year)
      else next.add(year)
      return next
    })
  }

  const openDetail = (assessmentId: string) => {
    navigate(`/customers/${customer.id}/assessments/${assessmentId}`)
  }

  const handleAdvance = async (assessment: Assessment, targetStatus: AssessmentStatus) => {
    if (targetStatus === 'filed') {
      setFilingAssessment(assessment)
      return
    }
    setAdvancingId(assessment.id)
    try {
      const updated = await getAssessmentService().updateStatus(assessment.id, targetStatus)
      setAssessments(prev => prev.map(a => (a.id === updated.id ? updated : a)))
      toast.success(
        'Status updated',
        `${assessment.itrType}: ${ASSESSMENT_STATUS_LABEL[targetStatus]}`
      )
    } catch (err) {
      toast.error(
        'Could not update status',
        err instanceof Error ? err.message : 'Please try again.'
      )
    } finally {
      setAdvancingId(null)
    }
  }

  const handleFilingSubmit = async (values: RecordFilingFormValues) => {
    if (!filingAssessment) return
    try {
      await getAssessmentService().update(filingAssessment.id, {
        acknowledgeNumber: values.acknowledgeNumber,
        filedDate: values.filedDate,
        refundAmount: values.refundAmount,
        demandAmount: values.demandAmount,
      })
      const updated = await getAssessmentService().updateStatus(filingAssessment.id, 'filed')
      setAssessments(prev => prev.map(a => (a.id === updated.id ? updated : a)))
      toast.success('Filing recorded', `${filingAssessment.itrType} marked as Filed`)
    } catch (err) {
      toast.error(
        'Could not record filing',
        err instanceof Error ? err.message : 'Please try again.'
      )
      throw err
    }
  }

  // Group by financial year, newest first.
  const groupedAssessments = assessments.reduce<Record<string, Assessment[]>>((acc, a) => {
    ;(acc[a.financialYear] ??= []).push(a)
    return acc
  }, {})

  const sortedYears = Object.keys(groupedAssessments).sort(
    (a, b) => Number(b.split('-')[0]) - Number(a.split('-')[0])
  )

  if (loading) {
    return (
      <div className="card card-elevated p-12 text-center">
        <span className="loading loading-spinner loading-sm" />
        <p className="text-text-secondary mt-3">Loading assessments…</p>
      </div>
    )
  }

  if (assessments.length === 0) {
    return (
      <>
        {!legacyAssessmentWritesEnabled && (
          <div
            className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200"
            role="status"
          >
            <strong>Legacy assessments — read only.</strong> {legacyAssessmentReadOnlyMessage}
          </div>
        )}
        <div className="card card-elevated p-12 text-center">
          <div className="w-16 h-16 bg-surface-overlay rounded-full flex items-center justify-center mx-auto mb-4">
            <FileText className="w-8 h-8 text-text-tertiary" />
          </div>
          <p className="text-lg font-medium text-text-primary mb-2">No Assessments Yet</p>
          <p className="text-text-secondary mb-6">
            {legacyAssessmentWritesEnabled
              ? 'Start a new tax assessment for this customer'
              : 'Create and continue work in the V2 return-preparation workspace.'}
          </p>
          {canCreate && (
            <button
              type="button"
              onClick={() => setIsModalOpen(true)}
              className="btn btn-primary btn-sm mx-auto flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Start New Assessment
            </button>
          )}
        </div>

        {canCreate && (
          <AssessmentModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            customerId={customer.id}
            customerPan={customer.pan}
            onSuccess={() => load()}
          />
        )}
      </>
    )
  }

  return (
    <div className="space-y-4">
      {!legacyAssessmentWritesEnabled && (
        <div
          className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200"
          role="status"
        >
          <strong>Legacy assessments — read only.</strong> {legacyAssessmentReadOnlyMessage}
        </div>
      )}
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-text-primary">Tax Assessments</h3>
        {canCreate && (
          <button
            type="button"
            onClick={() => setIsModalOpen(true)}
            className="btn btn-primary btn-sm flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Start New Assessment
          </button>
        )}
      </div>

      {/* Grouped by Financial Year */}
      <div className="space-y-3">
        {sortedYears.map(year => {
          const yearAssessments = groupedAssessments[year]
          const isExpanded = expandedYears.has(year)

          return (
            <div key={year} className="card card-elevated overflow-hidden">
              {/* Year Header */}
              <button
                type="button"
                onClick={() => toggleYear(year)}
                className="w-full p-4 flex items-center justify-between hover:bg-surface-overlay/50 transition-colors"
                aria-expanded={isExpanded}
              >
                <div className="flex items-center gap-3">
                  {isExpanded ? (
                    <ChevronDown className="w-5 h-5 text-text-secondary" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-text-secondary" />
                  )}
                  <span className="font-semibold text-text-primary">FY {year}</span>
                  <span className="text-sm text-text-secondary">
                    ({yearAssessments.length} assessment{yearAssessments.length > 1 ? 's' : ''})
                  </span>
                </div>
              </button>

              {/* Assessments List */}
              {isExpanded && (
                <div className="border-t border-surface-overlay">
                  {yearAssessments.map((assessment, idx) => (
                    <div
                      key={assessment.id}
                      className={`p-4 ${
                        idx < yearAssessments.length - 1 ? 'border-b border-surface-overlay' : ''
                      }`}
                    >
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2 flex-wrap">
                            <span className="font-semibold text-text-primary">
                              {assessment.itrType}
                            </span>
                            <span
                              className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-700 dark:bg-slate-800 dark:text-slate-300"
                              title="Legacy tax-assessment record"
                            >
                              Legacy
                            </span>
                            <span className="text-text-secondary">
                              AY {assessment.assessmentYear}
                            </span>
                            <StatusBadge status={assessment.status} />
                          </div>

                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                            <div>
                              <p className="text-text-tertiary">Due Date</p>
                              <p className="text-text-primary font-medium">
                                {formatDate(assessment.dueDate)}
                              </p>
                            </div>
                            {assessment.filedDate && (
                              <div>
                                <p className="text-text-tertiary">Filed Date</p>
                                <p className="text-text-primary font-medium">
                                  {formatDate(assessment.filedDate)}
                                </p>
                              </div>
                            )}
                            {assessment.taxableIncome != null && (
                              <div>
                                <p className="text-text-tertiary">Taxable Income</p>
                                <p className="text-text-primary font-medium">
                                  {formatCurrency(assessment.taxableIncome)}
                                </p>
                              </div>
                            )}
                            {assessment.refundAmount != null && (
                              <div>
                                <p className="text-text-tertiary">Refund</p>
                                <p className="text-green-600 dark:text-green-400 font-medium">
                                  {formatCurrency(assessment.refundAmount)}
                                </p>
                              </div>
                            )}
                            {assessment.demandAmount != null && (
                              <div>
                                <p className="text-text-tertiary">Demand</p>
                                <p className="text-red-600 dark:text-red-400 font-medium">
                                  {formatCurrency(assessment.demandAmount)}
                                </p>
                              </div>
                            )}
                          </div>

                          {assessment.acknowledgeNumber && (
                            <p className="text-sm text-text-secondary mt-2">
                              Ack No:{' '}
                              <code className="font-mono bg-surface-overlay px-1 rounded">
                                {assessment.acknowledgeNumber}
                              </code>
                            </p>
                          )}
                        </div>

                        {(() => {
                          const currentIdx = ASSESSMENT_STATUS_FLOW.indexOf(assessment.status)
                          const nextStatus =
                            currentIdx >= 0 && currentIdx < ASSESSMENT_STATUS_FLOW.length - 1
                              ? ASSESSMENT_STATUS_FLOW[currentIdx + 1]
                              : null
                          const canAdvance =
                            canUpdateStatus && (nextStatus !== 'filed' || (canFile && canApprove))

                          return (
                            <div className="flex items-center gap-2 flex-wrap">
                              {nextStatus && canAdvance && (
                                <button
                                  type="button"
                                  onClick={() => handleAdvance(assessment, nextStatus)}
                                  disabled={advancingId === assessment.id}
                                  className="btn btn-primary btn-sm flex items-center gap-1.5"
                                >
                                  {advancingId === assessment.id ? (
                                    <span className="loading loading-spinner loading-xs" />
                                  ) : (
                                    <Check className="w-3.5 h-3.5" />
                                  )}
                                  Mark {ASSESSMENT_STATUS_LABEL[nextStatus]}
                                </button>
                              )}
                              {canUpdate && (
                                <button
                                  type="button"
                                  onClick={() => setLinkingAssessment(assessment)}
                                  className="btn btn-outline btn-sm flex items-center gap-1.5"
                                  title="Link client documents"
                                >
                                  <Link2 className="w-3.5 h-3.5" />
                                  Link Documents
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={() => openDetail(assessment.id)}
                                className="btn btn-ghost btn-sm"
                              >
                                View Details
                              </button>
                            </div>
                          )
                        })()}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )
        })}
      </div>

      {canCreate && (
        <AssessmentModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          customerId={customer.id}
          customerPan={customer.pan}
          onSuccess={() => load()}
        />
      )}

      {filingAssessment && (
        <RecordFilingModal
          isOpen={!!filingAssessment}
          onClose={() => setFilingAssessment(null)}
          onSubmit={handleFilingSubmit}
          initialAcknowledgeNumber={filingAssessment.acknowledgeNumber}
          initialFiledDate={filingAssessment.filedDate}
          initialRefundAmount={filingAssessment.refundAmount}
          initialDemandAmount={filingAssessment.demandAmount}
        />
      )}

      {linkingAssessment && (
        <LinkDocumentModal
          isOpen={!!linkingAssessment}
          onClose={() => setLinkingAssessment(null)}
          customerId={customer.id}
          assessmentId={linkingAssessment.id}
          assessmentYear={linkingAssessment.assessmentYear}
          alreadyLinkedDocumentIds={[]}
          onLinked={() => {
            load()
          }}
        />
      )}
    </div>
  )
}

export default function CustomerAssessmentsTab(props: CustomerAssessmentsTabProps) {
  const { hasPermission } = useAuth()
  const canReadCases = isReturnPreparationV2Enabled() && hasPermission('txh.assessment.case.read')
  const canReadLegacy = hasPermission('txh.assessment.read')
  const clientOwnedHistoryEnabled = isClientOwnedAssessmentPagesEnabled()
  const canReadClientOwnedHistory = clientOwnedHistoryEnabled && canReadLegacy
  return (
    <div className="space-y-6">
      {canReadCases && <ReturnCasePortfolioPage customerId={props.customer.id} />}
      {canReadClientOwnedHistory ? (
        <ClientOwnedAssessmentHistory customerId={props.customer.id} customerType="individual" />
      ) : (
        canReadLegacy && <LegacyCustomerAssessmentsTab {...props} />
      )}
    </div>
  )
}
